import React, { useState, useEffect, useCallback } from "react";
import "../../Styles/EventsTable.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { eventsAPI, fileAPI } from "../../api/index"; // adjust path to match your project
import { MANDALS } from "./EventsForm"; // reuse the same id->name list — keeps them in sync

// ---------- Excel template settings ----------
// Headings follow the Add Event form fields (same order as the form).
// CONFIRM these with your backend if you add a bulk upload for events.
const TEMPLATE_HEADERS = [
  "Event Title",
  "Event Type",
  "Venue",
  "Chief Guest / Organizer",
  "Village",
  "Mandal",
  "Date",
  "Time",
  "Expected Attendance",
  "Status",
  "Event Description",
];

const TEMPLATE_COLUMN_WIDTHS = [
  { wch: 34 }, // Event Title
  { wch: 20 }, // Event Type
  { wch: 30 }, // Venue
  { wch: 28 }, // Chief Guest / Organizer
  { wch: 22 }, // Village
  { wch: 20 }, // Mandal
  { wch: 14 }, // Date
  { wch: 12 }, // Time
  { wch: 22 }, // Expected Attendance
  { wch: 14 }, // Status
  { wch: 55 }, // Event Description
];

const TEMPLATE_INSTRUCTIONS = [
  ["Column", "Required", "Allowed values / Rules"],
  ["Event Title", "Yes", "3-150 characters. Letters, numbers, spaces and . , - & ' ( ) / : only"],
  ["Event Type", "Yes", "Inauguration, Public Meeting, Review Meeting, Felicitation, Grievance Camp, Cultural Event, Other"],
  ["Venue", "Yes", "3-150 characters"],
  ["Chief Guest / Organizer", "No", "Optional. 3-100 characters. Alphabets, spaces and . , & ' - only"],
  ["Village", "Yes", "Alphabets and spaces only (2-100 characters)"],
  ["Mandal", "Yes", MANDALS.map((m) => m.name).join(", ")],
  ["Date", "Yes", "Format: YYYY-MM-DD (e.g. 2026-10-03)"],
  ["Time", "Yes", "24-hour format HH:MM (e.g. 16:30 for 4:30 PM)"],
  ["Expected Attendance", "No", "Optional. Whole number from 1 to 1000000"],
  ["Status", "Yes", "Upcoming, Completed, Cancelled"],
  ["Event Description", "No", "Optional. If filled: 10-1000 characters"],
  ["Photo", "-", "Photos cannot be added in Excel. Upload them through the Add Event form"],
];

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const mandalName = (id) => MANDALS.find((m) => m.id === id)?.name || "—";

const MONTH_NAMES = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];
const DAY_NAMES = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

const BADGE_COLORS = ["evt123-badge-blue", "evt123-badge-green", "evt123-badge-purple"];

const parseEventDate = (isoDate) => {
  if (!isoDate) return { day: "", month: "", dateNum: "", year: "" };
  const d = new Date(isoDate + "T00:00:00");
  return {
    day: DAY_NAMES[d.getDay()],
    month: MONTH_NAMES[d.getMonth()],
    dateNum: String(d.getDate()).padStart(2, "0"),
    year: String(d.getFullYear()),
  };
};

const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}-${month}-${year}`;
};

const formatDisplayTime = (isoTime) => {
  if (!isoTime) return "";
  const [hStr, mStr] = isoTime.split(":");
  let h = parseInt(hStr, 10);
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${mStr} ${suffix}`;
};

const PAGE_SIZE = 9;

const EventsTable = () => {
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All Types");
  const [mandalFilter, setMandalFilter] = useState("All Mandals");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [previewItem, setPreviewItem] = useState(null);
  const openPreview = (item) => setPreviewItem(item);
  const closePreview = () => setPreviewItem(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await eventsAPI.getAll({});
      const data = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setEvents(data);
    } catch (err) {
      console.error("Failed to load events:", err);
      setLoadError(err.response?.data?.detail || "Could not load events.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (!previewItem) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closePreview();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [previewItem]);

  /* ---------- Download Excel template (headings only) ---------- */
  const handleDownloadTemplate = () => {
    try {
      // Sheet 1: Template (headings only, ready to fill)
      const templateSheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
      templateSheet["!cols"] = TEMPLATE_COLUMN_WIDTHS;

      // Sheet 2: Instructions (allowed values and rules)
      const instructionSheet = XLSX.utils.aoa_to_sheet(TEMPLATE_INSTRUCTIONS);
      instructionSheet["!cols"] = [{ wch: 26 }, { wch: 10 }, { wch: 95 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, templateSheet, "Template");
      XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");

      XLSX.writeFile(workbook, "Events_Template.xlsx");
      toast.success("Template downloaded successfully");
    } catch (err) {
      console.error("Template download failed:", err);
      toast.error("Could not download the template");
    }
  };

  const resetFilters = () => {
    setSearch("");
    setTypeFilter("All Types");
    setMandalFilter("All Mandals");
    setStatusFilter("All Status");
    setCurrentPage(1);
  };

  const filteredEvents = events.filter((item) => {
    const searchLower = search.trim().toLowerCase();

    const matchesSearch =
      searchLower === "" ||
      (item.event_title || "").toLowerCase().includes(searchLower) ||
      (item.venue || "").toLowerCase().includes(searchLower);

    const matchesType = typeFilter === "All Types" || item.event_type === typeFilter;

    const matchesMandal =
      mandalFilter === "All Mandals" || String(item.mandal_id) === mandalFilter;

    const matchesStatus =
      statusFilter === "All Status" ||
      (item.status || "").toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesType && matchesMandal && matchesStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * PAGE_SIZE;
  const pageEvents = filteredEvents.slice(startIndex, startIndex + PAGE_SIZE);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const totalEvents = events.length;
  const upcomingEvents = events.filter((e) => e.status === "upcoming").length;
  const completedEvents = events.filter((e) => e.status === "completed").length;
  const cancelledEvents = events.filter((e) => e.status === "cancelled").length;

  const getStatusClass = (statusLabel) => {
    switch (statusLabel) {
      case "Upcoming":
        return "evt123-status-upcoming";
      case "Completed":
        return "evt123-status-completed";
      case "Cancelled":
        return "evt123-status-cancelled";
      default:
        return "";
    }
  };

  const openEditModal = (item) => {
    setEditItem({
      id: item.id,
      title: item.event_title || "",
      type: item.event_type || "",
      venue: item.venue || "",
      guest: item.chief_guest || "",
      village: "", // backend doesn't return village name, only village_id
      mandalId: item.mandal_id ? String(item.mandal_id) : "", // pre-fill from the id we DO have
      date: item.event_date || "",
      time: item.event_time ? item.event_time.slice(0, 5) : "",
      attendance: item.expected_attendance ?? "",
      status: capitalize(item.status),
      description: item.description || "",
      photo_url: item.photo_url || null,
    });
    setEditFile(null);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditItem(null);
    setEditFile(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) setEditFile(selected);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      let photoUrl = editItem.photo_url;
      if (editFile) {
        const uploadRes = await fileAPI.upload(editFile, "events");
        photoUrl =
          uploadRes.data?.url ||
          uploadRes.data?.file_url ||
          uploadRes.data?.photo_url ||
          photoUrl;
      }

      const resolvedMandalName = editItem.mandalId
        ? MANDALS.find((m) => String(m.id) === String(editItem.mandalId))?.name
        : undefined;

      await eventsAPI.update(editItem.id, {
        event_title: editItem.title,
        event_type: editItem.type,
        venue: editItem.venue,
        chief_guest: editItem.guest || null,
        village_name: editItem.village || undefined,
        mandal_name: resolvedMandalName,
        event_date: editItem.date,
        event_time: editItem.time ? `${editItem.time}:00` : null,
        expected_attendance: editItem.attendance ? Number(editItem.attendance) : null,
        status: editItem.status.toLowerCase(),
        description: editItem.description || null,
        photo_url: photoUrl,
      });

      await fetchEvents();
      closeEditModal();
    } catch (err) {
      console.error("Failed to update event:", err);
      alert(err.response?.data?.detail || "Something went wrong while saving changes.");
    } finally {
      setSavingEdit(false);
    }
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await eventsAPI.delete(deleteId);
      await fetchEvents();
      closeDeleteModal();
    } catch (err) {
      console.error("Failed to delete event:", err);
      alert(err.response?.data?.detail || "Could not delete this event.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="evt123-page">
      <div className="evt123-header">
        <div>
          <h2 className="evt123-title">Events</h2>
          <p className="evt123-subtitle">
            Manage and organize all public events and programs.
          </p>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="evt123-add-btn"
            onClick={handleDownloadTemplate}
            style={{ background: "#16a34a" }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Download Template
          </button>

          <button
            className="evt123-add-btn"
            onClick={() => navigate("/dashboard/EventsForm")}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            Add New Event
          </button>
        </div>
      </div>

      {loadError && <div className="evt123-no-data">{loadError}</div>}

      <div className="evt123-stats-grid">
        <div className="evt123-stat-card evt123-stat-card-blue">
          <div className="evt123-stat-icon evt123-stat-icon-blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div className="evt123-stat-info">
            <span className="evt123-stat-label">Total Events</span>
            <span className="evt123-stat-value">{totalEvents}</span>
            <span className="evt123-stat-sub">All time</span>
          </div>
        </div>

        <div className="evt123-stat-card evt123-stat-card-green">
          <div className="evt123-stat-icon evt123-stat-icon-green">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <path d="M8.5 12.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="evt123-stat-info">
            <span className="evt123-stat-label">Upcoming</span>
            <span className="evt123-stat-value">{upcomingEvents}</span>
            <span className="evt123-stat-sub">Upcoming Events</span>
          </div>
        </div>

        <div className="evt123-stat-card evt123-stat-card-purple">
          <div className="evt123-stat-icon evt123-stat-icon-purple">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <path d="M8.5 12.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="evt123-stat-info">
            <span className="evt123-stat-label">Completed</span>
            <span className="evt123-stat-value">{completedEvents}</span>
            <span className="evt123-stat-sub">Completed Events</span>
          </div>
        </div>

        <div className="evt123-stat-card evt123-stat-card-red">
          <div className="evt123-stat-icon evt123-stat-icon-red">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div className="evt123-stat-info">
            <span className="evt123-stat-label">Cancelled</span>
            <span className="evt123-stat-value">{cancelledEvents}</span>
            <span className="evt123-stat-sub">Cancelled Events</span>
          </div>
        </div>
      </div>

      <div className="evt123-filters">
        <div className="evt123-search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search Event..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <select
          className="evt123-select"
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Types</option>
          <option>Inauguration</option>
          <option>Public Meeting</option>
          <option>Review Meeting</option>
          <option>Felicitation</option>
          <option>Grievance Camp</option>
          <option>Cultural Event</option>
          <option>Other</option>
        </select>

        <select
          className="evt123-select"
          value={mandalFilter}
          onChange={(e) => {
            setMandalFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="All Mandals">All Mandals</option>
          {MANDALS.map((m) => (
            <option key={m.id} value={String(m.id)}>
              {m.name}
            </option>
          ))}
        </select>

        <select
          className="evt123-select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Status</option>
          <option>Upcoming</option>
          <option>Completed</option>
          <option>Cancelled</option>
        </select>

        <button className="evt123-reset-btn" onClick={resetFilters}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 4v6h6M20 20v-6h-6M4.5 15a8 8 0 0014.5 3.5M19.5 9A8 8 0 005 5.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Clear Filters
        </button>
      </div>

      <div className="evt123-list">
        {loading ? (
          <div className="evt123-no-data">Loading...</div>
        ) : pageEvents.length === 0 ? (
          <div className="evt123-no-data">No events found.</div>
        ) : (
          pageEvents.map((item, index) => {
            const { day, month, dateNum, year } = parseEventDate(item.event_date);
            const statusLabel = capitalize(item.status);
            const badgeColor = BADGE_COLORS[index % BADGE_COLORS.length];

            return (
              <div className="evt123-card" key={item.id}>
                <div className={`evt123-date-badge ${badgeColor}`}>
                  <span className="evt123-date-month">{month}</span>
                  <span className="evt123-date-num">{dateNum}</span>
                  <span className="evt123-date-year">{year}</span>
                  <span className="evt123-date-day">{day}</span>
                </div>

                <div className="evt123-thumb">
                  {item.photo_url ? (
                    <button
                      type="button"
                      className="evt123-thumb-btn"
                      onClick={() => openPreview(item)}
                      title="View photo"
                    >
                      <img src={item.photo_url} alt={item.event_title} />
                    </button>
                  ) : (
                    <div className="evt123-thumb-fallback" />
                  )}
                </div>

                <div className="evt123-card-body">
                  <h3 className="evt123-card-title">{item.event_title}</h3>
                  <span className="evt123-type-tag">{item.event_type || "—"}</span>

                  <div className="evt123-card-meta">
                    <div className="evt123-meta-row">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 22s7-6.5 7-12a7 7 0 10-14 0c0 5.5 7 12 7 12z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinejoin="round"
                        />
                        <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
                      </svg>
                      {item.venue || "—"}
                    </div>

                    <div className="evt123-meta-row">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1M9 13h1M14 9h1M14 13h1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {/* Village name still unavailable (only village_id comes back);
                          mandal name is now resolved from mandal_id via MANDALS */}
                      {item.village_id != null ? `Village #${item.village_id}` : "—"}, {mandalName(item.mandal_id)}
                    </div>
                  </div>
                </div>

                <div className="evt123-card-schedule">
                  <div className="evt123-meta-row">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {formatDisplayTime(item.event_time)}
                  </div>

                  <div className="evt123-meta-row">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                    {formatDisplayDate(item.event_date)}
                  </div>
                </div>

                <div className="evt123-card-status">
                  <span className={`evt123-status-badge ${getStatusClass(statusLabel)}`}>
                    {statusLabel === "Upcoming" && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                        <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    )}
                    {statusLabel === "Completed" && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                        <path d="M8.5 12.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    {statusLabel === "Cancelled" && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                        <path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    )}
                    {statusLabel}
                  </span>
                </div>

                <div className="evt123-card-actions">
                  <button className="evt123-edit-btn" onClick={() => openEditModal(item)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Edit
                  </button>

                  <button className="evt123-delete-btn" onClick={() => openDeleteModal(item.id)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="evt123-pagination-bar">
        <span className="evt123-pagination-info">
          Showing {pageEvents.length === 0 ? 0 : startIndex + 1} to{" "}
          {startIndex + pageEvents.length} of {filteredEvents.length} events
        </span>

        <div className="evt123-pagination-controls">
          <button
            className="evt123-page-arrow"
            onClick={() => handlePageChange(safePage - 1)}
          >
            &lt;
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`evt123-page-num ${safePage === p ? "evt123-active" : ""}`}
              onClick={() => handlePageChange(p)}
            >
              {p}
            </button>
          ))}

          <button
            className="evt123-page-arrow"
            onClick={() => handlePageChange(safePage + 1)}
          >
            &gt;
          </button>
        </div>
      </div>

      {previewItem && (
        <div className="evt123-modal-overlay" onClick={closePreview}>
          <div
            className="evt123-modal evt123-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="evt123-modal-header">
              <h3>{previewItem.event_title}</h3>
              <button className="evt123-modal-close" onClick={closePreview}>
                &times;
              </button>
            </div>

            <div className="evt123-preview-body">
              {previewItem.photo_url ? (
                <img
                  src={previewItem.photo_url}
                  alt={previewItem.event_title}
                  className="evt123-preview-img"
                />
              ) : (
                <div className="evt123-no-data">No photo available.</div>
              )}
            </div>

            {previewItem.description && (
              <p className="evt123-preview-desc">{previewItem.description}</p>
            )}
          </div>
        </div>
      )}

      {showEditModal && editItem && (
        <div className="evt123-modal-overlay" onClick={closeEditModal}>
          <div className="evt123-modal" onClick={(e) => e.stopPropagation()}>
            <div className="evt123-modal-header">
              <h3>Edit Event</h3>
              <button className="evt123-modal-close" onClick={closeEditModal}>
                &times;
              </button>
            </div>

            <form className="evt123-modal-body" onSubmit={handleEditSave}>
              <div className="evt123-form-group">
                <label>Event Title</label>
                <input
                  type="text"
                  name="title"
                  value={editItem.title}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="evt123-form-row">
                <div className="evt123-form-group">
                  <label>Type</label>
                  <select name="type" value={editItem.type} onChange={handleEditChange}>
                    <option>Inauguration</option>
                    <option>Public Meeting</option>
                    <option>Review Meeting</option>
                    <option>Felicitation</option>
                    <option>Grievance Camp</option>
                    <option>Cultural Event</option>
                    <option>Other</option>
                  </select>
                </div>

                <div className="evt123-form-group">
                  <label>Status</label>
                  <select name="status" value={editItem.status} onChange={handleEditChange}>
                    <option>Upcoming</option>
                    <option>Completed</option>
                    <option>Cancelled</option>
                  </select>
                </div>
              </div>

              <div className="evt123-form-group">
                <label>Venue</label>
                <input
                  type="text"
                  name="venue"
                  value={editItem.venue}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="evt123-form-group">
                <label>Chief Guest / Organizer</label>
                <input
                  type="text"
                  name="guest"
                  value={editItem.guest}
                  onChange={handleEditChange}
                />
              </div>

              <div className="evt123-form-row">
                <div className="evt123-form-group">
                  <label>Village (leave blank to keep current)</label>
                  <input
                    type="text"
                    name="village"
                    placeholder="Only fill in to change"
                    value={editItem.village}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="evt123-form-group">
                  <label>Mandal</label>
                  <select name="mandalId" value={editItem.mandalId} onChange={handleEditChange}>
                    <option value="">Choose Mandal</option>
                    {MANDALS.map((m) => (
                      <option key={m.id} value={String(m.id)}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="evt123-form-row">
                <div className="evt123-form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    name="date"
                    value={editItem.date}
                    onChange={handleEditChange}
                    required
                  />
                </div>

                <div className="evt123-form-group">
                  <label>Time</label>
                  <input
                    type="time"
                    name="time"
                    value={editItem.time}
                    onChange={handleEditChange}
                    required
                  />
                </div>
              </div>

              <div className="evt123-form-row">
                <div className="evt123-form-group">
                  <label>Expected Attendance</label>
                  <input
                    type="number"
                    name="attendance"
                    value={editItem.attendance}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="evt123-form-group">
                  <label>Replace Photo (optional)</label>
                  <input type="file" accept="image/*" onChange={handleEditFileChange} />
                </div>
              </div>

              <div className="evt123-form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  rows="3"
                  value={editItem.description}
                  onChange={handleEditChange}
                />
              </div>

              <div className="evt123-modal-footer">
                <button
                  type="button"
                  className="evt123-btn-cancel"
                  onClick={closeEditModal}
                  disabled={savingEdit}
                >
                  Cancel
                </button>
                <button type="submit" className="evt123-btn-save" disabled={savingEdit}>
                  {savingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="evt123-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="evt123-modal evt123-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="evt123-confirm-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h3 className="evt123-confirm-title">Delete this event?</h3>
            <p className="evt123-confirm-text">
              This action cannot be undone. Are you sure you want to delete
              this event?
            </p>

            <div className="evt123-confirm-footer">
              <button className="evt123-btn-cancel" onClick={closeDeleteModal} disabled={deleting}>
                Cancel
              </button>
              <button className="evt123-btn-delete" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "OK, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsTable;